import React, { FunctionComponent, useMemo, useState } from 'react'
import {
  MultiSelectSearch,
  SearchInput,
  SelectSearch,
  useSelectSearchFiltering,
} from '../../../../shared'
import { PatientTimelineFiltersProps } from './PatientTimelineFilters.model'
import { computeTeamsDoctorsOptions } from '../../../../../misc/team.utilities'
import classnames from 'classnames'
import {
  useDebouncedFilters,
  useDocumentCategoryOptions,
  useTagSearch,
} from '../../../../../hooks/utils'

export const PatientTimelineFilters: FunctionComponent<PatientTimelineFiltersProps> = ({
  currentUser,
  teams,
  tags,
  filters,
  setFilters,
}) => {
  const { search, category, doctorId, tagIds } = filters
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  const [tagOptions, tagValues] = useTagSearch(tagIds, tags)
  const doctorsOptions = useMemo(
    () => (currentUser ? computeTeamsDoctorsOptions(teams, currentUser, 'Mes événements') : []),
    [currentUser, teams],
  )
  const doctorValue = doctorsOptions.find(({ value }) => value === doctorId)
  const [doctorText, setDoctorText, filteredDoctorOptions] = useSelectSearchFiltering(
    doctorValue,
    doctorsOptions,
  )

  const hasTeamMates = doctorsOptions.length > 1
  const categoriesOptions = useDocumentCategoryOptions()
  const categoriesValue = categoriesOptions.find(({ value }) => value === category)
  const [categoriesText, setCategoriesText, filteredCategoriesOptions] = useSelectSearchFiltering(
    categoriesValue,
    categoriesOptions,
  )

  useDebouncedFilters(debouncedSearch, (search) => setFilters({ ...filters, search }))

  return (
    <div
      className={classnames(
        'p-4 lg:p-10 lg:pt-0 grid grid-flow-row grid-cols-1 lg:grid-flow-col gap-4',
        {
          'lg:grid-cols-4': hasTeamMates,
          'lg:grid-cols-3': !hasTeamMates,
        },
      )}
    >
      <SearchInput
        placeholder="Nom de l'évènement"
        value={debouncedSearch}
        onChange={({ target: { value } }) => setDebouncedSearch(value)}
        testId="input-medical_event-by_name-search"
      />
      <MultiSelectSearch
        placeholder="Rechercher par label"
        icon="tag"
        value={tagValues}
        options={tagOptions}
        onSelect={(selected) => {
          const tagIds = selected.map(({ value }) => value)
          setFilters({ ...filters, tagIds })
        }}
        testId="multi_select_search-patient_timeline-medical_event-by_tag-search"
      />
      {hasTeamMates && (
        <SelectSearch
          placeholder="Médecin"
          icon="profile"
          value={doctorValue}
          options={filteredDoctorOptions}
          onSelect={(selected) => {
            setFilters({ ...filters, doctorId: selected?.value ?? undefined })
          }}
          textValue={doctorText}
          onTextChange={setDoctorText}
          onFocus={() => setDoctorText('')}
          testId="select_search-patient_timeline-by_team_member-search"
        />
      )}
      <SelectSearch
        placeholder="Type de document"
        icon={categoriesValue?.icon ?? 'documentText'}
        value={categoriesValue}
        options={filteredCategoriesOptions}
        onSelect={(selected) => setFilters({ ...filters, category: selected?.value ?? undefined })}
        textValue={categoriesText}
        onTextChange={setCategoriesText}
        onFocus={() => setCategoriesText('')}
        testId="select_search-patient_timeline-medical_event-by_doc_type-search"
      />
    </div>
  )
}
